import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import FileUploader from "../../../Components/FileUploader";
import useAuthStore from "../../../store/useAuthStore";

const REQUIRED_CSV_HEADERS = ["Invoice Number", "Date", "Client Name", "Item", "Quantity", "Unit Price"];
const INVOICE_STATUSES = ["Unpaid", "Paid", "Overdue", "Draft"];

const FIELDS = [
  { key: "Invoice Number",  type: "text",         required: true  },
  { key: "Date",            type: "date",          required: true  },
  { key: "Due Date",        type: "date",          required: false },
  { key: "Client Name",     type: "text",          required: true  },
  { key: "Client Email",    type: "email",         required: false },
  { key: "Client Phone",    type: "text",          required: false },
  { key: "Item",            type: "text",          required: true  },
  { key: "Quantity",        type: "number",        required: true  },
  { key: "Unit Price",      type: "number",        required: true  },
  { key: "Amount",          type: "number",        required: false },
  { key: "Tax Rate",        type: "number",        required: false },
  { key: "Discount",        type: "number",        required: false },
  { key: "Status",          type: "statusSelect",  required: false },
];

function validateRow(row) {
  const errors = {};
  if (!row["Invoice Number"]) errors["Invoice Number"] = "Required";
  if (!row["Date"])           errors["Date"] = "Required";
  if (!row["Client Name"])    errors["Client Name"] = "Required";
  if (!row["Item"])           errors["Item"] = "Required";

  const qty = parseFloat(row["Quantity"]);
  if (!row["Quantity"] || isNaN(qty) || qty <= 0)
    errors["Quantity"] = "Must be > 0";

  const price = parseFloat(row["Unit Price"]);
  if (row["Unit Price"] === undefined || row["Unit Price"] === "" || isNaN(price) || price < 0)
    errors["Unit Price"] = "Must be ≥ 0";

  if (row["Amount"] !== undefined && row["Amount"] !== "") {
    const amt = parseFloat(row["Amount"]);
    if (isNaN(amt) || amt < 0) errors["Amount"] = "Must be a number ≥ 0";
  }
  if (row["Tax Rate"] !== undefined && row["Tax Rate"] !== "") {
    const tr = parseFloat(row["Tax Rate"]);
    if (isNaN(tr) || tr < 0 || tr > 100) errors["Tax Rate"] = "Must be 0–100";
  }
  if (row["Discount"] !== undefined && row["Discount"] !== "") {
    const d = parseFloat(row["Discount"]);
    if (isNaN(d) || d < 0) errors["Discount"] = "Must be ≥ 0";
  }
  if (row["Status"] && !INVOICE_STATUSES.includes(row["Status"])) {
    errors["Status"] = `Must be one of: ${INVOICE_STATUSES.join(", ")}`;
  }
  return errors;
}

function BulkImportInvoice() {
  const [rows, setRows]               = useState([]);
  const [rowErrors, setRowErrors]     = useState({});
  const [activeTab, setActiveTab]     = useState("upload");
  const [file, setFile]               = useState(null);
  const [fileError, setFileError]     = useState("");
  const [editingIdx, setEditingIdx]   = useState(null);
  const [editDraft, setEditDraft]     = useState({});
  const [draftErrors, setDraftErrors] = useState({});
  const [importing, setImporting]     = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filter, setFilter]           = useState("all");

  const navigate = useNavigate();
  const { token } = useAuthStore();

  // Recompute errors when rows change
  useEffect(() => {
    if (rows.length === 0) return;
    const errs = {};
    rows.forEach((row, idx) => {
      const e = validateRow(row);
      if (Object.keys(e).length > 0) errs[idx] = e;
    });
    setRowErrors(errs);
  }, [rows]);

  const readFile = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(ws, { raw: false }));
        } catch (err) { reject(err); }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(f);
    });

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setFileError("");
    setRows([]);
    setImportResult(null);
    setEditingIdx(null);
    if (!selectedFile) return;
    try {
      const data = await readFile(selectedFile);
      if (data.length === 0) { setFileError("File is empty."); return; }
      const headers = Object.keys(data[0]).map((h) => h.trim());
      const missing = REQUIRED_CSV_HEADERS.filter(
        (h) => !headers.some((fh) => fh.toLowerCase() === h.toLowerCase())
      );
      if (missing.length > 0) {
        setFileError(`Missing required columns: ${missing.join(", ")}`);
        return;
      }
      setRows(data);
    } catch {
      setFileError("Error reading file. Please check the format and try again.");
    }
  };

  const handleNext = () => {
    if (!file || rows.length === 0 || fileError) return;
    setActiveTab("validate");
  };

  const handleEditRow = (idx) => {
    const draft = { ...rows[idx] };
    setEditingIdx(idx);
    setEditDraft(draft);
    setDraftErrors(validateRow(draft));
  };

  const handleDraftChange = (field, value) => {
    const updated = { ...editDraft, [field]: value };
    setEditDraft(updated);
    setDraftErrors(validateRow(updated));
  };

  const handleSaveRow = () => {
    const newRows = [...rows];
    newRows[editingIdx] = { ...editDraft };
    setRows(newRows);
    setEditingIdx(null);
    setEditDraft({});
    setDraftErrors({});
  };

  const handleCancelEdit = () => {
    setEditingIdx(null);
    setEditDraft({});
    setDraftErrors({});
  };

  const handleDeleteRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) { setEditingIdx(null); setEditDraft({}); }
    else if (editingIdx !== null && editingIdx > idx) setEditingIdx(editingIdx - 1);
  };

  // Group rows by Invoice Number to compute valid invoice count
  const invoiceGroups = useMemo(() => {
    const map = new Map();
    rows.forEach((row, idx) => {
      const key = (row["Invoice Number"] || "").toString().trim() || `__anon_${idx}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(idx);
    });
    return map;
  }, [rows]);

  const validInvoiceCount = useMemo(() => {
    let count = 0;
    invoiceGroups.forEach((idxList) => {
      const anyBad = idxList.some((idx) => !!rowErrors[idx]);
      if (!anyBad) count++;
    });
    return count;
  }, [invoiceGroups, rowErrors]);

  const errorRowCount = useMemo(
    () => rows.filter((_, idx) => !!rowErrors[idx]).length,
    [rows, rowErrors]
  );

  const filteredIndexes = useMemo(
    () =>
      rows.map((_, idx) => idx).filter((idx) => {
        if (filter === "valid")  return !rowErrors[idx];
        if (filter === "errors") return !!rowErrors[idx];
        return true;
      }),
    [rows, rowErrors, filter]
  );

  const handleImport = async () => {
    // Only send rows that belong to fully-valid invoice groups
    const validGroupKeys = new Set();
    invoiceGroups.forEach((idxList, key) => {
      if (!idxList.some((idx) => !!rowErrors[idx])) validGroupKeys.add(key);
    });

    const validRows = rows.filter((row) => {
      const key = (row["Invoice Number"] || "").toString().trim();
      return validGroupKeys.has(key);
    });

    if (validRows.length === 0) return;
    setImporting(true);
    try {
      const res = await fetch("/api/bulkImportInvoice", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rows: validRows }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Import failed");
      setImportResult(result);
      if (result.imported > 0) setTimeout(() => navigate("/invoice"), 3000);
    } catch (err) {
      setImportResult({ error: err.message });
    } finally {
      setImporting(false);
    }
  };

  const renderCell = (row, idx, field, fieldDef) => {
    const isEditing = editingIdx === idx;
    const value = isEditing ? (editDraft[field] ?? "") : (row[field] ?? "");
    const err = isEditing ? draftErrors[field] : rowErrors[idx]?.[field];

    if (!isEditing) {
      return (
        <td key={field} className={`px-3 py-2 text-sm align-top ${err ? "bg-red-50" : ""}`}>
          <span className={err ? "text-red-700" : "text-gray-700"}>{value?.toString() || "—"}</span>
          {err && <p className="text-xs text-red-500 mt-0.5 whitespace-normal max-w-[140px]">{err}</p>}
        </td>
      );
    }

    const inputClass = `w-full text-sm border rounded px-2 py-1 focus:outline-none focus:ring-1 ${
      err ? "border-red-400 focus:ring-red-400" : "border-gray-300 focus:ring-blue-500"
    }`;

    let input;
    if (fieldDef.type === "statusSelect") {
      input = (
        <select value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass}>
          <option value="">— Select —</option>
          {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    } else if (fieldDef.type === "date") {
      input = (
        <input type="date" value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    } else if (fieldDef.type === "number") {
      input = (
        <input type="number" value={value} min="0" step="0.01" onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    } else if (fieldDef.type === "email") {
      input = (
        <input type="email" value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    } else {
      input = (
        <input type="text" value={value} onChange={(e) => handleDraftChange(field, e.target.value)} className={inputClass} />
      );
    }

    return (
      <td key={field} className="px-3 py-2 align-top">
        <div className="min-w-[110px]">
          {input}
          {err && <p className="text-xs text-red-500 mt-0.5">{err}</p>}
        </div>
      </td>
    );
  };

  // Determine if a row starts a new invoice group (for visual separator)
  const isGroupBoundary = (idx) => {
    if (idx === 0) return false;
    const cur  = (rows[idx]["Invoice Number"]     || "").toString().trim();
    const prev = (rows[idx - 1]["Invoice Number"] || "").toString().trim();
    return cur !== prev;
  };

  return (
    <>
      <div className="flex items-center justify-between px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <span className="text-gray-700 font-semibold text-lg">Bulk Import Invoices</span>
        <button onClick={() => navigate("/invoice")} className="text-blue-500 hover:underline text-sm">
          ← Back to Invoices
        </button>
      </div>

      <div className="mx-auto p-6 bg-white rounded-lg shadow-sm">
        {/* Tabs */}
        <div className="flex border-b mb-6">
          {[
            { id: "upload",   label: "1 · Upload" },
            { id: "validate", label: "2 · Validate & Edit" },
          ].map((tab) => {
            const locked = tab.id === "validate" && (!file || !!fileError || rows.length === 0);
            return (
              <button
                key={tab.id}
                onClick={() => (tab.id === "validate" ? handleNext() : setActiveTab("upload"))}
                disabled={locked}
                className={`px-5 py-2.5 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : locked
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Upload Tab ── */}
        {activeTab === "upload" && (
          <div>
            {fileError && (
              <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded">
                {fileError}
              </div>
            )}

            <h2 className="text-base font-medium text-gray-800 mb-4">Upload your invoices file</h2>

            <FileUploader
              onFileSelect={handleFileSelect}
              acceptedFormats={["csv", "tsv", "xls", "xlsx"]}
              maxSizeMB={25}
            />

            <p className="text-sm text-gray-500 mt-3">
              Download a{" "}
              <a href="/samples/invoice_import_template.csv" className="text-blue-600 hover:underline" download>
                sample CSV template
              </a>{" "}
              to see the expected format.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg mt-4 text-sm text-gray-700">
              <p className="font-medium text-gray-800 mb-1">Required columns</p>
              <p>
                <strong>Invoice Number</strong> · <strong>Date</strong> ·{" "}
                <strong>Client Name</strong> · <strong>Item</strong> ·{" "}
                <strong>Quantity</strong> · <strong>Unit Price</strong>
              </p>
              <p className="text-gray-500 mt-1">
                Optional: Due Date · Client Email/Phone · Amount · Tax Rate · Discount · Status
              </p>
              <p className="text-gray-500 mt-1">
                Multiple rows sharing the same <strong>Invoice Number</strong> are merged into one invoice with multiple line items.
              </p>
            </div>

            {file && rows.length > 0 && !fileError && (
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {rows.length} rows · {invoiceGroups.size} invoices detected from <strong>{file.name}</strong>
                </span>
                <button
                  onClick={handleNext}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md font-medium"
                >
                  Next: Validate &amp; Edit →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Validate & Edit Tab ── */}
        {activeTab === "validate" && (
          <div>
            {importResult && (
              <div
                className={`mb-4 p-4 rounded-lg text-sm ${
                  importResult.error
                    ? "bg-red-50 border-l-4 border-red-500 text-red-700"
                    : importResult.imported > 0
                    ? "bg-green-50 border-l-4 border-green-500 text-green-700"
                    : "bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700"
                }`}
              >
                {importResult.error ? (
                  <p>{importResult.error}</p>
                ) : (
                  <>
                    <p className="font-semibold">Import Complete</p>
                    <p>
                      Imported: {importResult.imported} invoice{importResult.imported !== 1 ? "s" : ""} ·{" "}
                      Failed: {importResult.failed}
                    </p>
                    {importResult.errors?.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-xs">
                        {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    )}
                    {importResult.imported > 0 && (
                      <p className="mt-1 text-xs">Redirecting to Invoices…</p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="mb-3 p-3 bg-amber-50 border-l-4 border-amber-400 text-amber-800 text-sm rounded">
              Rows with the same <strong>Invoice Number</strong> are grouped into one invoice.
              If any row in a group has an error, that entire invoice is skipped.
            </div>

            {/* Summary + Filter */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-700">
                  {rows.length} rows · {invoiceGroups.size} invoices
                </span>
                <span className="text-green-600 font-medium">✓ {validInvoiceCount} valid invoice{validInvoiceCount !== 1 ? "s" : ""}</span>
                {invoiceGroups.size - validInvoiceCount > 0 && (
                  <span className="text-red-600 font-medium">
                    ✗ {invoiceGroups.size - validInvoiceCount} with errors
                  </span>
                )}
              </div>
              <div className="flex gap-1.5">
                {[
                  { id: "all",    label: "All" },
                  { id: "valid",  label: "Valid rows" },
                  { id: "errors", label: "Error rows" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      filter === f.id
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8">#</th>
                    {FIELDS.map((f) => (
                      <th key={f.key} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                        {f.key}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-3 w-28" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredIndexes.length === 0 ? (
                    <tr>
                      <td colSpan={FIELDS.length + 3} className="text-center py-10 text-gray-400 text-sm">
                        No rows to display
                      </td>
                    </tr>
                  ) : (
                    filteredIndexes.map((idx) => {
                      const row = rows[idx];
                      const errs = rowErrors[idx] || {};
                      const isEditing = editingIdx === idx;
                      const hasError = Object.keys(errs).length > 0;

                      // Show a thin separator between invoice groups
                      const showGroupBorder =
                        filter === "all" && isGroupBoundary(idx);

                      return (
                        <tr
                          key={idx}
                          className={[
                            isEditing ? "bg-blue-50" : hasError ? "bg-red-50/40" : "",
                            showGroupBorder ? "border-t-2 border-blue-200" : "",
                          ].join(" ")}
                        >
                          <td className="px-3 py-2 text-xs text-gray-400 align-top">{idx + 1}</td>
                          {FIELDS.map((f) => renderCell(row, idx, f.key, f))}
                          <td className="px-3 py-2 align-top">
                            {!isEditing && (
                              hasError ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Error</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Valid</span>
                              )
                            )}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {isEditing ? (
                              <div className="flex flex-col gap-1">
                                <button onClick={handleSaveRow} className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 font-medium">Save</button>
                                <button onClick={handleCancelEdit} className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50">Cancel</button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button onClick={() => handleEditRow(idx)} className="px-2 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50">Edit</button>
                                <button onClick={() => handleDeleteRow(idx)} className="px-2 py-1 border border-red-200 text-red-500 text-xs rounded hover:bg-red-50" title="Remove row">✕</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <button onClick={() => setActiveTab("upload")} className="text-sm text-gray-500 hover:text-gray-700">
                ← Back to Upload
              </button>
              <button
                onClick={handleImport}
                disabled={importing || validInvoiceCount === 0}
                className={`px-6 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                  importing || validInvoiceCount === 0
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {importing
                  ? "Importing…"
                  : `Import ${validInvoiceCount} Valid Invoice${validInvoiceCount !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default BulkImportInvoice;
